/**
 * Quiz View Component
 *
 * Main quiz playing interface with map, prompt card, and settings panel.
 * Handles the active quiz-taking experience.
 */

import { QuizMap } from "@/components/quiz/QuizMap";
import { MapCredits } from "@/components/shared/MapCredits";
import { MapSettingsPanel } from "@/components/quiz/MapSettingsPanel";
import { Card, CardContent } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import type {
  QuizFeature,
  QuizRuntimeConfig,
  QuizAttemptRecord,
} from "@/stores/useQuizStore";
import type { GeoJsonDataset } from "@/data/geojsonDatasets";

// Type for the current prompt (from selectCurrentPrompt)
type CurrentPrompt = {
  promptId: string;
  featureId: string | undefined;
  validFeatureIds: string[];
  feature: QuizFeature;
  promptParts: { key: string; value: string }[];
  promptText: string;
  attemptsRemaining: number;
} | null;

interface QuizViewProps {
  features: QuizFeature[];
  config: QuizRuntimeConfig | null;
  currentPrompt: CurrentPrompt;
  currentIndex: number;
  order: string[];
  progress: number;
  lastAnswer: QuizAttemptRecord | null;
  attempts: QuizAttemptRecord[];
  dataset: GeoJsonDataset | undefined;
  onFeatureClick: (featureId: string) => void;
  showCompletionLabels?: boolean;
}

export function QuizView({
  features,
  config,
  currentPrompt,
  currentIndex,
  order,
  progress,
  lastAnswer,
  attempts,
  dataset,
  onFeatureClick,
  showCompletionLabels = false,
}: QuizViewProps) {
  return (
    <>
      <QuizMap
        features={features}
        onFeatureClick={onFeatureClick}
        attempts={attempts}
        showCompletionLabels={showCompletionLabels}
      />

      {/* Map Credits - Floating */}
      {dataset?.credits && dataset.credits.length > 0 && (
        <MapCredits variant="floating" credits={dataset.credits} />
      )}

      {/* Map Settings Panel - Desktop: top-right, Mobile: below prompt */}
      <div className="absolute top-4 right-4 z-dropdown pointer-events-none sm:pointer-events-auto sm:block hidden">
        <div className="pointer-events-auto">
          <MapSettingsPanel />
        </div>
      </div>

      {/* Prompt Overlay (Floating) - Centered at top, responsive layout */}
      {currentPrompt && (
        <>
          <div className="absolute top-4 left-1/2 -translate-x-1/2 z-dropdown w-full max-w-3xl px-4 pointer-events-none">
            <Card className="bg-card/95 backdrop-blur-md shadow-xl transition-all duration-300 pointer-events-auto">
              <CardContent className="p-4">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  {/* Question */}
                  <div className="flex-1 min-w-0">
                    <h3 className="text-xs font-medium text-muted-foreground mb-1">
                      Question {currentIndex + 1} of {order.length}
                    </h3>
                    <p className="text-xl md:text-2xl font-bold text-primary">
                      {currentPrompt.promptText}
                    </p>
                  </div>

                  {/* Attempts & Feedback */}
                  <div className="flex items-center gap-2 sm:gap-3 text-sm flex-shrink-0">
                    <span className="text-muted-foreground whitespace-nowrap">
                      Attempts: {lastAnswer?.attemptsUsed || 0}/{config?.maxAttemptsPerPrompt || 1}
                    </span>
                    {lastAnswer && (
                      <span
                        className={cn(
                          "font-semibold whitespace-nowrap",
                          lastAnswer.status === "correct"
                            ? "text-green-600"
                            : "text-orange-600"
                        )}
                      >
                        {lastAnswer.status === "correct" ? "✓ Correct!" : "Try again!"}
                      </span>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Map Settings Panel - Mobile only: below prompt, hides when locked */}
          <div className="sm:hidden absolute top-[120px] right-4 z-dropdown pointer-events-none">
            <div className="pointer-events-auto">
              <MapSettingsPanel hideOnMobileWhenLocked />
            </div>
          </div>
        </>
      )}
    </>
  );
}
